import { computed, ref, watch } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { noop, useDebounceFn, useEventListener } from '@vueuse/core';
import OrdersService from '@/api/services/orders.service';
import { isRetryableRequestError } from '@/api/retry.utils';
import { OrderMode, OrderStatus, OrderType } from '@/enums/OrderEnums';
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { AlertType } from '@/interfaces/IAlert';
import type { IAuthData } from '@/interfaces/IAuthData';
import type { ICustomer } from '@/interfaces/ICustomer';
import type { IItem } from '@/interfaces/IItem';
import type { IOrder } from '@/interfaces/IOrder';
import { roundPrice } from '@/utils';
import { adaptItemOptions } from '@/domain/pos/option-adapter';
import {
  ISelectedOptionsMap,
  validateSelectedOptions,
} from '@/domain/pos/item-options-validation';
import { computeItemUnitPrice } from '@/domain/pos/pricing/item-price';
import { resolveDeliveryCost } from '@/domain/pos/pricing/delivery-zones';
import { resolveAllowedOrderType } from '@/domain/pos/order-type-policies';
import {
  buildSelectedOptionsSignature,
  getOrderEditRequiresCancel,
  normalizeDeliveryCoord,
  orderItemToCartLine,
  rekeyCollidingCartLines,
  shouldEnforceStrictOutsideZoneForDraft,
  type IRekeyedCartLines,
} from '@/domain/pos/order-lines';
import { compareOrderContent } from '@/domain/pos/draft-content-parity';
import { getOrderTitleForOrder } from '@/domain/pos/order-formatting';
import { PosV2Error } from '@/domain/pos/pos-error';
import type {
  IPosCartLine,
  IPosDraft,
  TPosDraftMode,
} from '@/domain/pos/pos-types';
import {
  emptyPosTotals,
  usePosDraftTotals,
} from '@/composition/use-pos-draft-totals';
import { useSocketStore } from '@/composition/use-socket';
import { useMenuStore } from '@/composition/use-menu';
import { useUi } from '@/composition/use-ui';
import { i18n } from '@/plugins/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useOrdersStore } from '@/stores/orders.store';

export interface IPosDraftValidationError {
  key: string;
  params?: Record<string, string>;
}

const PERSISTENCE_KEY = 'posV2Drafts';
const PERSISTENCE_VERSION = 1;

/** Empty (no cart lines) create drafts untouched for longer than this are dropped at restore. */
const EMPTY_CREATE_DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

interface IPersistedViewStub {
  id: string;
  mode: 'view';
  orderId: number;
  /** Optional for storage written before the field existed (falls back to Seat). */
  type?: OrderType;
}

interface IPersistedState {
  version: number;
  restaurantId: number;
  branchId: number;
  activeDraftId: string | null;
  drafts: Array<IPosDraft | IPersistedViewStub>;
}

export const ORDER_TYPE_TO_CF_TYPE: Record<OrderType, string> = {
  [OrderType.Seat]: 's',
  [OrderType.Away]: 't',
  [OrderType.Delivery]: 'd',
};

/**
 * Draft is "empty and unedited" when it can be safely reused/retargeted
 * without risking user data loss (ported from -pos usePosOrderDrafts).
 */
export function isEmptyUneditedDraft(
  d: IPosDraft | null | undefined
): d is IPosDraft {
  if (!d) return false;

  const noMeta =
    !d.tableId &&
    !(d.customerData.name || d.customerData.phone) &&
    !d.deliveryInfo?.address &&
    !d.userComment &&
    !d.clubMemberId;

  return (
    !d.orderId &&
    d.mode === 'create' &&
    d.isDirty === false &&
    d.cartLines.length === 0 &&
    noMeta &&
    (d.totals?.total ?? 0) === 0
  );
}

export const usePosDraftsStore = defineStore('posDrafts', () => {
  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const socketStore = useSocketStore();
  const menuStore = useMenuStore();
  const draftTotals = usePosDraftTotals();
  const ui = useUi();

  const drafts = ref<IPosDraft[]>([]);
  const activeDraftId = ref<string | null>(null);

  const activeDraft = computed<IPosDraft | null>(() => {
    if (!activeDraftId.value) return null;
    return drafts.value.find((d) => d.id === activeDraftId.value) ?? null;
  });

  /**
   * True when the active EDIT draft's baseline is stale: the live order
   * changed from another device (or left the pending set) since the draft was
   * hydrated. The UI blocks Save and offers a refresh while this is true.
   */
  const activeDraftEditConflict = computed<boolean>(() => {
    const draft = activeDraft.value;
    if (!draft || draft.mode !== 'edit' || !draft.orderId) return false;

    // Orders not loaded yet — don't flash the banner.
    if (ordersStore.orders === null) return false;

    const order = ordersStore.orders.find((o) => o.id === draft.orderId);
    // Left the pending set: archived/canceled/paid elsewhere.
    if (!order) return true;

    // Legacy persisted draft without a baseline: must refresh before saving.
    if (!draft.baselineUpdatedAt) return true;

    return (
      new Date(order.updatedAt).getTime() !==
      new Date(draft.baselineUpdatedAt).getTime()
    );
  });

  //#region Internal helpers

  function findDraft(id: string): IPosDraft | null {
    return drafts.value.find((d) => d.id === id) ?? null;
  }

  /**
   * Stamps user work on the draft (drives the empty-draft TTL and age chip).
   * Only user mutations and order hydration (`applyOrderPatch`,
   * `bindCreatedOrder`) may call this — nothing on the create request path
   * does: `persistCreate` treats a stamp newer than the request start as
   * "edited during the request".
   */
  function touchDraft(draft: IPosDraft): void {
    draft.lastTouchedAt = Date.now();
  }

  function getActiveCustomFields(type: OrderType) {
    const fields = restaurantStore.customFields ?? [];
    return fields.filter((f) =>
      f.orderTypes?.includes(ORDER_TYPE_TO_CF_TYPE[type])
    );
  }

  /**
   * Builds a fresh values map for the active fields of `type`, preserving any
   * existing values (always a NEW object — Vue 2 reactivity safety).
   */
  function seedCustomFieldValues(
    type: OrderType,
    existing?: Record<string, string>
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const field of getActiveCustomFields(type)) {
      const key = String(field.id);
      out[key] = existing?.[key] ?? '';
    }
    return out;
  }

  function resolveDefaultDraftType(): OrderType {
    const branch = restaurantStore.selectedBranch;
    return (
      resolveAllowedOrderType(
        {
          tableOrders: branch?.tableOrders ?? null,
          takeAway: branch?.takeAway ?? null,
          delivery: branch?.delivery ?? null,
        },
        OrderType.Seat
      ) ?? OrderType.Seat
    );
  }

  /** Every key initialized explicitly (Vue 2 can't track keys added later). */
  function createEmptyDraft(
    type: OrderType,
    mode: TPosDraftMode = 'create'
  ): IPosDraft {
    return {
      id: uuidv4(),
      localId: uuidv4(),
      mode,
      type,
      orderId: undefined,
      baselineUpdatedAt: null,
      tableId: undefined,
      covers: null,
      customerId: null,
      clubMemberId: null,
      clubMemberName: null,
      clubMemberPoints: null,
      clubMemberCash: null,
      clubCode: null,
      customerData: { name: '', phone: '' },
      deliveryInfo: null,
      baselineDeliveryLat: null,
      baselineDeliveryLng: null,
      userComment: '',
      customFieldValues: seedCustomFieldValues(type),
      cartLines: [],
      nextIndex: 0,
      discounts: [],
      totals: emptyPosTotals(),
      isDirty: false,
      lastTouchedAt: Date.now(),
      createSubmittedAt: null,
    };
  }

  async function resolveOrderById(orderId: number): Promise<IOrder> {
    const fromStore = ordersStore.orders?.find((o) => o.id === orderId);
    if (fromStore) return fromStore;

    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) throw new PosV2Error('posV2.errOrderNotFound');

    try {
      return await OrdersService.v2GetOrderById(restaurantId, orderId);
    } catch (e) {
      // Only a real 404 means "not found" — a timeout/500 must surface as a
      // server error, not tell the operator the order doesn't exist.
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        throw new PosV2Error('posV2.errOrderNotFound');
      }
      throw e;
    }
  }

  function findOrderByLocalId(localId: string): IOrder | null {
    return ordersStore.orders?.find((o) => o.localId === localId) ?? null;
  }

  /**
   * Resolves with the store's copy of the order carrying `localId` (fed by the
   * v2:order socket event, reconnect snapshots and periodic reloads). Never
   * rejects. Callers MUST call stop(): a watcher created inside an action is
   * not bound to any component/effect scope in Vue 2.7 (teardown is
   * idempotent, so stopping twice is safe).
   */
  function waitForOrderByLocalId(localId: string): {
    promise: Promise<IOrder>;
    stop: () => void;
  } {
    let stopWatch: () => void = noop;
    const promise = new Promise<IOrder>((resolve) => {
      const existing = findOrderByLocalId(localId);
      if (existing) {
        resolve(existing);
        return;
      }
      stopWatch = watch(
        () => findOrderByLocalId(localId),
        (order) => {
          if (order) {
            stopWatch();
            resolve(order);
          }
        }
      );
    });
    return { promise, stop: () => stopWatch() };
  }

  function getTaxContext() {
    return {
      taxPercent: restaurantStore.restaurant?.tax ?? null,
      taxMode: restaurantStore.restaurantSettings?.taxMode ?? TaxDisplayMode.NoTax,
    };
  }

  //#endregion

  //#region Draft lifecycle

  function createDraft(type?: OrderType): string {
    const draft = createEmptyDraft(type ?? resolveDefaultDraftType());
    drafts.value.push(draft);
    activeDraftId.value = draft.id;
    return draft.id;
  }

  /**
   * Returns false (refuses) for dirty unsaved carts unless `force` — the UI
   * confirms with the user and re-calls with force=true.
   */
  function closeDraft(id: string, force = false): boolean {
    const draft = findDraft(id);
    if (!draft) return false;

    if (!force && draft.isDirty && draft.cartLines.length > 0 && !draft.orderId) {
      return false;
    }

    const index = drafts.value.findIndex((d) => d.id === id);
    if (index !== -1) drafts.value.splice(index, 1);

    if (activeDraftId.value === id) {
      if (drafts.value.length > 0) {
        activeDraftId.value = drafts.value[0].id;
      } else {
        createDraft();
      }
    }

    return true;
  }

  function setActiveDraft(id: string): void {
    if (findDraft(id)) activeDraftId.value = id;
  }

  function setMode(id: string, mode: TPosDraftMode): void {
    const draft = findDraft(id);
    if (!draft) return;
    draft.mode = mode;
    touchDraft(draft);
  }

  function updateDraft(id: string, changes: Partial<IPosDraft>): void {
    const draft = findDraft(id);
    if (!draft) return;

    const typeChanged =
      typeof changes.type !== 'undefined' && changes.type !== draft.type;

    // Persisted orders cannot change type (backend contract, defense-in-depth).
    if (typeChanged && draft.mode !== 'create') {
      throw new Error('ORDER_TYPE_IMMUTABLE');
    }

    Object.assign(draft, changes);
    // Any user-initiated patch counts as work, regardless of which fields it
    // carries (touchDraft is not part of `changes`, so it can't be clobbered).
    touchDraft(draft);

    // Allow caller to explicitly control isDirty; otherwise mark as dirty.
    if (typeof changes.isDirty === 'undefined') {
      draft.isDirty = true;
    }

    if (typeChanged) {
      // Clear cross-type meta, keep customerData, reseed custom fields.
      if (draft.type !== OrderType.Seat) {
        draft.tableId = undefined;
        draft.covers = null;
      }
      if (draft.type !== OrderType.Delivery) {
        draft.deliveryInfo = null;
      }
      draft.customFieldValues = seedCustomFieldValues(
        draft.type,
        draft.customFieldValues
      );
    }

    if (
      changes.cartLines ||
      typeof changes.deliveryInfo !== 'undefined' ||
      changes.type ||
      changes.discounts
    ) {
      recomputeTotals(id);
    }
  }

  //#endregion

  //#region Cart operations

  /**
   * Comparison-only normalization ('' ≡ null); stored values stay verbatim.
   */
  function normalizeComment(comment: string | null | undefined): string {
    return (comment ?? '').trim();
  }

  function addCartLine(
    draftId: string,
    item: IItem,
    optionValues: ISelectedOptionsMap,
    quantity: number,
    userComment?: string
  ): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    const itemOptions = adaptItemOptions(item);
    const validation = validateSelectedOptions(itemOptions, optionValues);
    if (!validation.ok) {
      throw validation.firstError
        ? new PosV2Error(validation.firstError.key, validation.firstError.params)
        : new PosV2Error('posV2.reviewOptions');
    }
    const sanitized = validation.sanitized;

    // Merge rule: same item + same normalized comment + same options signature.
    const candidateSignature = buildSelectedOptionsSignature(sanitized);
    const normalizedComment = normalizeComment(userComment);

    const existingLine = draft.cartLines.find((line) => {
      if (line.itemId !== item.id) return false;
      if (normalizeComment(line.userComment) !== normalizedComment)
        return false;
      return (
        buildSelectedOptionsSignature(line.optionValues) === candidateSignature
      );
    });

    if (existingLine) {
      // Keep the line's index AND its stored userComment verbatim.
      existingLine.quantity += quantity;
      existingLine.totalPrice = roundPrice(
        existingLine.unitPrice * existingLine.quantity
      );
      draft.isDirty = true;
      touchDraft(draft);
      recomputeTotals(draftId);
      return;
    }

    const { taxPercent, taxMode } = getTaxContext();
    const unitPrice = computeItemUnitPrice({
      item,
      itemOptions,
      selectedOptions: sanitized,
      taxPercent,
      taxMode,
    });

    const line: IPosCartLine = {
      index: draft.nextIndex++,
      itemId: item.id,
      quantity,
      unitPrice,
      totalPrice: roundPrice(unitPrice * quantity),
      // New lines store trimmed comment or '' (matches today's create payload
      // contract — never undefined).
      userComment: normalizedComment,
      optionValues: sanitized,
    };

    draft.cartLines.push(line);
    draft.isDirty = true;
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  /**
   * Updates a line IN PLACE (keeps `index` — it is the backend diff identity).
   * Deliberately no merge-reindex (differs from -pos).
   */
  function editCartLine(
    draftId: string,
    index: number,
    optionValues: ISelectedOptionsMap,
    quantity: number,
    userComment?: string
  ): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    const line = draft.cartLines.find((l) => l.index === index);
    if (!line) return;

    const item = menuStore.itemMap?.[line.itemId];
    if (!item) throw new PosV2Error('posV2.errItemUnavailable');

    const itemOptions = adaptItemOptions(item);
    const validation = validateSelectedOptions(itemOptions, optionValues);
    if (!validation.ok) {
      throw validation.firstError
        ? new PosV2Error(validation.firstError.key, validation.firstError.params)
        : new PosV2Error('posV2.reviewOptions');
    }
    const sanitized = validation.sanitized;

    const { taxPercent, taxMode } = getTaxContext();
    const unitPrice = computeItemUnitPrice({
      item,
      itemOptions,
      selectedOptions: sanitized,
      taxPercent,
      taxMode,
    });

    // Preserve the stored comment verbatim when its content didn't change
    // ('' vs null matters for the backend diff of persisted lines).
    const newComment = normalizeComment(userComment);
    if (newComment !== normalizeComment(line.userComment)) {
      line.userComment = newComment;
    }

    line.quantity = quantity;
    line.unitPrice = unitPrice;
    line.totalPrice = roundPrice(unitPrice * quantity);
    line.optionValues = sanitized;

    draft.isDirty = true;
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  /** Removes by line `index` identity; surviving indexes are NEVER reassigned. */
  function removeCartLine(draftId: string, index: number): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    const position = draft.cartLines.findIndex((l) => l.index === index);
    if (position === -1) return;

    draft.cartLines.splice(position, 1);
    draft.isDirty = true;
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  function setQuantity(draftId: string, index: number, quantity: number): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    const line = draft.cartLines.find((l) => l.index === index);
    if (!line || quantity < 1) return;

    line.quantity = quantity;
    line.totalPrice = roundPrice(line.unitPrice * quantity);
    draft.isDirty = true;
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  /** Keeps `nextIndex` monotonic so re-added lines get fresh identities. */
  function clearCart(draftId: string): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    draft.cartLines = [];
    draft.isDirty = true;
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  //#endregion

  //#region Customer / order hydration

  function applyCustomer(draftId: string, customer: ICustomer | null): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    if (customer) {
      const changes: Partial<IPosDraft> = {
        customerId: customer.id,
        customerData: {
          name: customer.name,
          phone: customer.tel ?? '',
        },
      };

      if (draft.type === OrderType.Delivery) {
        changes.deliveryInfo = customer.address
          ? {
              address: customer.address,
              address2: customer.address2 ?? '',
              addressComment: customer.addressComment ?? '',
              lat: customer.lat ?? undefined,
              lng: customer.lng ?? undefined,
            }
          : null;
      }

      updateDraft(draftId, changes);
      return;
    }

    const changes: Partial<IPosDraft> = {
      customerId: null,
      customerData: { name: '', phone: '' },
    };
    if (draft.type === OrderType.Delivery) {
      changes.deliveryInfo = null;
    }
    updateDraft(draftId, changes);
  }

  /**
   * Attaches (or detaches with null) a Club member to the draft. `code` is
   * the live code that identified them (null for lookup picks / detach): the
   * create payload sends it once so the server consumes it for the new order.
   */
  function applyClubMember(
    draftId: string,
    member: {
      id: number;
      name: string;
      pointsBalance: number;
      cashTotal: number;
    } | null,
    code: string | null
  ): void {
    updateDraft(draftId, {
      clubMemberId: member?.id ?? null,
      clubMemberName: member?.name ?? null,
      clubMemberPoints: member?.pointsBalance ?? null,
      clubMemberCash: member?.cashTotal ?? null,
      clubCode: member ? code : null,
    });
  }

  /**
   * Maps a persisted order onto the draft fields it owns. Cart lines exclude
   * soft-canceled items; comments and indexes round-trip verbatim so a no-op
   * save produces an all-`equal` backend diff. The return type is inferred on
   * purpose: every key is present, which `bindCreatedOrder` relies on.
   */
  function orderToDraftPatch(order: IOrder) {
    const orderItems = order.orderItems ?? [];

    const cartLines = orderItems
      .filter((oi) => !oi.canceledAt)
      .slice()
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map(orderItemToCartLine);

    // nextIndex considers ALL items (incl. canceled) — reusing a canceled
    // item's index would collide in the backend diff.
    const maxIndex = orderItems.reduce(
      (max, oi) => Math.max(max, oi.index ?? 0),
      -1
    );

    const loadedLat = normalizeDeliveryCoord(order.deliveryLat);
    const loadedLng = normalizeDeliveryCoord(order.deliveryLng);

    const customFieldValues = seedCustomFieldValues(order.type);
    for (const field of order.orderFields ?? []) {
      customFieldValues[String(field.fieldId)] = field.value ?? '';
    }

    return {
      type: order.type,
      orderId: order.id,
      // IOrder.updatedAt is TYPED Date but is an ISO string at runtime;
      // new Date().toISOString() normalizes both shapes canonically.
      baselineUpdatedAt: order.updatedAt
        ? new Date(order.updatedAt).toISOString()
        : null,
      tableId: order.tableId ?? undefined,
      covers: order.covers ?? null,
      customerId: order.customerId ?? null,
      clubMemberId: order.clubMemberId ?? null,
      clubMemberName: order.clubMember?.name ?? null,
      clubMemberPoints: order.clubMember?.pointsBalance ?? null,
      clubMemberCash: null,
      // A persisted order never carries a code (consumed at creation, or
      // useless once the order exists).
      clubCode: null,
      customerData: {
        name: order.userName || '',
        phone: order.userTel || order.userWhatsapp || '',
      },
      deliveryInfo: order.userAddress
        ? {
            address: order.userAddress,
            address2: order.userAddress2 || '',
            addressComment: order.userAddressComments || '',
            lat: loadedLat ?? undefined,
            lng: loadedLng ?? undefined,
          }
        : null,
      baselineDeliveryLat: loadedLat,
      baselineDeliveryLng: loadedLng,
      userComment: order.comment || '',
      customFieldValues,
      cartLines,
      nextIndex: maxIndex + 1,
      discounts: (order.discounts ?? []).map((d) => ({
        isPercentage: d.isPercentage,
        value: d.value,
        metadata: d.metadata ?? null,
      })),
      isDirty: false,
    } satisfies Partial<IPosDraft>;
  }

  type TOrderDraftPatch = ReturnType<typeof orderToDraftPatch>;

  function applyOrderPatch(draftId: string, patch: TOrderDraftPatch): void {
    const draft = findDraft(draftId);
    if (!draft) return;

    Object.assign(draft, patch);
    touchDraft(draft);
    recomputeTotals(draftId);
  }

  /** Hydrates a draft from a persisted order. */
  function loadFromOrder(draftId: string, order: IOrder): void {
    applyOrderPatch(draftId, orderToDraftPatch(order));
  }

  function isOrderEditable(order: IOrder): boolean {
    return (
      order.status !== OrderStatus.Finished &&
      order.status !== OrderStatus.Canceled &&
      order.status !== OrderStatus.Declined &&
      !order.archived
    );
  }

  /**
   * Re-hydrates an edit draft from the live order, resetting its conflict
   * baseline. If the refreshed order is no longer editable (finished/canceled/
   * declined/archived elsewhere), the tab falls back to the read-only view.
   */
  async function refreshEditBaseline(draftId: string): Promise<void> {
    const draft = findDraft(draftId);
    if (!draft?.orderId) return;

    const order = await resolveOrderById(draft.orderId);

    // The tab may have been closed while the order was being fetched.
    const live = findDraft(draftId);
    if (!live) return;

    loadFromOrder(live.id, order);

    if (!isOrderEditable(order)) {
      setMode(live.id, 'view');
    }
  }

  // In-flight hydration claims per draft id: a failing openOrderTab must not
  // close a draft that a concurrent call still plans to hydrate. Accepted
  // trade-off: if the owner fails first and the claiming call then fails too,
  // the claimed draft survives as a plain empty create tab (the claimer never
  // owned it, so its cleanup is a no-op) — benign, and better than closing a
  // tab another call might still need.
  const hydrationClaims = new Map<string, number>();

  function claimHydration(draftId: string): void {
    hydrationClaims.set(draftId, (hydrationClaims.get(draftId) ?? 0) + 1);
  }

  function releaseHydration(draftId: string): void {
    const count = (hydrationClaims.get(draftId) ?? 1) - 1;
    if (count <= 0) hydrationClaims.delete(draftId);
    else hydrationClaims.set(draftId, count);
  }

  /**
   * Opens (or focuses) a tab for a persisted order. Reuses the active tab when
   * it's empty and unedited; never steals a tab holding user work.
   */
  async function openOrderTab(
    params: { orderId: number },
    mode: TPosDraftMode = 'view'
  ): Promise<string> {
    const existing = drafts.value.find((d) => d.orderId === params.orderId);

    if (existing) {
      activeDraftId.value = existing.id;
      if (mode === 'edit' && existing.mode === 'view') {
        const order = await resolveOrderById(params.orderId);
        // The tab may have been closed while the order was being fetched.
        const live = findDraft(existing.id);
        if (!live) throw new PosV2Error('posV2.errOrderNotFound');
        // Re-check the mode after the await: a concurrent call may have
        // upgraded the tab already, and re-hydrating would wipe any edits
        // the user made in the meantime.
        if (live.mode === 'view') {
          loadFromOrder(live.id, order);
          live.mode = 'edit';
        }
      }
      return existing.id;
    }

    let target = isEmptyUneditedDraft(activeDraft.value)
      ? activeDraft.value
      : null;
    let createdFresh = false;

    if (!target) {
      const id = createDraft();
      target = findDraft(id);
      createdFresh = true;
    }

    // Tracks the draft this call owns (created itself) so cleanup can close
    // it — but only while it's still empty AND no concurrent call has an
    // in-flight claim on it: even an owned draft can become another call's
    // reuse target (createDraft focuses it), and closing it under that call
    // would make its post-await re-check fail spuriously.
    let ownedDraftId = createdFresh ? target!.id : null;
    const targetId = target!.id;

    function cleanupOwnedDraft(): void {
      if (!ownedDraftId) return;
      const foreignClaims =
        (hydrationClaims.get(ownedDraftId) ?? 0) -
        (ownedDraftId === targetId ? 1 : 0); // own claim is still held here
      if (foreignClaims > 0) return;
      const owned = findDraft(ownedDraftId);
      if (owned && isEmptyUneditedDraft(owned)) closeDraft(ownedDraftId, true);
    }

    claimHydration(targetId);
    try {
      const order = await resolveOrderById(params.orderId);

      // Re-checks after the await: the draft list may have changed while the
      // order was being fetched (concurrent openOrderTab calls, closed tabs).

      // Another call may have already opened a tab for this same order:
      // focus it instead of hydrating a duplicate (one tab per order).
      const concurrent = drafts.value.find(
        (d) => d.id !== targetId && d.orderId === params.orderId
      );
      if (concurrent) {
        cleanupOwnedDraft();
        // Honor the edit-mode upgrade exactly like the pre-await branch.
        if (mode === 'edit' && concurrent.mode === 'view') {
          loadFromOrder(concurrent.id, order);
          concurrent.mode = 'edit';
        }
        activeDraftId.value = concurrent.id;
        return concurrent.id;
      }

      // The tab may have been closed while the order was being fetched:
      // writing activeDraftId to a removed id would blank the workspace.
      let live = findDraft(targetId);
      if (!live) throw new PosV2Error('posV2.errOrderNotFound');

      // A racing call hydrated our own target with this same order: behave
      // like the pre-await existing-tab branch (upgrade to edit only, never
      // downgrade an edit tab back to view).
      if (live.orderId === params.orderId) {
        if (mode === 'edit' && live.mode === 'view') {
          loadFromOrder(live.id, order);
          live.mode = 'edit';
        }
        activeDraftId.value = live.id;
        return live.id;
      }

      // The target may have been hydrated with a different order or received
      // user input meanwhile — never steal it, open a fresh tab instead. This
      // applies to created targets too (see ownedDraftId note above); a
      // genuinely untouched fresh draft is still empty and passes through.
      if (!isEmptyUneditedDraft(live)) {
        const id = createDraft();
        ownedDraftId = id;
        live = findDraft(id);
        if (!live) throw new PosV2Error('posV2.errOrderNotFound');
      }

      loadFromOrder(live.id, order);
      live.mode = mode;
      activeDraftId.value = live.id;
      return live.id;
    } catch (e) {
      cleanupOwnedDraft();
      throw e;
    } finally {
      releaseHydration(targetId);
    }
  }

  /**
   * Type write-back for lazily-hydrated view stubs: keeps the tab label in
   * sync with the fetched order without dirtying the draft or running the
   * create/edit type-change side effects (which would also throw
   * ORDER_TYPE_IMMUTABLE for non-create drafts).
   */
  function syncViewDraftType(draftId: string, type: OrderType): void {
    const draft = findDraft(draftId);
    if (!draft || draft.mode !== 'view' || draft.type === type) return;
    draft.type = type;
  }

  //#endregion

  //#region Validation + totals

  function getValidationError(
    draft: IPosDraft
  ): IPosDraftValidationError | null {
    if (draft.cartLines.length === 0) {
      return { key: 'posV2.errCartEmpty' };
    }

    if (draft.type === OrderType.Seat && !draft.tableId) {
      return { key: 'posV2.errTableRequired' };
    }

    // Backend rule for manual orders: Away/Delivery require a name.
    if (
      (draft.type === OrderType.Away || draft.type === OrderType.Delivery) &&
      !draft.customerData.name.trim()
    ) {
      return { key: 'posV2.errNameRequired' };
    }

    if (
      draft.type === OrderType.Delivery &&
      !draft.deliveryInfo?.address?.trim()
    ) {
      return { key: 'posV2.errAddressRequired' };
    }

    // Create only: the backend requires numeric lat/lng on POST but never
    // validates deliveryInfo on PUT — edits of legacy coord-less delivery
    // orders must stay saveable.
    if (draft.type === OrderType.Delivery && draft.mode === 'create') {
      const lat = draft.deliveryInfo?.lat;
      const lng = draft.deliveryInfo?.lng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return { key: 'posV2.errAddressLocation' };
      }
    }

    for (const field of getActiveCustomFields(draft.type)) {
      if (!field.mandatory) continue;
      const value = draft.customFieldValues[String(field.id)] ?? '';
      if (!value.trim()) {
        return {
          key: 'posV2.errCustomFieldRequired',
          params: { field: field.title },
        };
      }
    }

    if (draft.type === OrderType.Delivery) {
      const branch = restaurantStore.selectedBranch;
      const lat = draft.deliveryInfo?.lat;
      const lng = draft.deliveryInfo?.lng;
      const hasValidCoords =
        typeof lat === 'number' &&
        Number.isFinite(lat) &&
        typeof lng === 'number' &&
        Number.isFinite(lng);

      const shouldEnforce = shouldEnforceStrictOutsideZoneForDraft({
        mode: draft.mode,
        baselineLat: draft.baselineDeliveryLat,
        baselineLng: draft.baselineDeliveryLng,
        nextLat: lat,
        nextLng: lng,
      });

      if (
        shouldEnforce &&
        branch?.zonedDelivery &&
        branch?.zonedFee &&
        hasValidCoords
      ) {
        try {
          resolveDeliveryCost({
            baseDeliveryCost: branch.deliveryCost ?? 0,
            zonedDelivery: true,
            zonedFee: true,
            deliveryInfo: { lat, lng },
            deliveryZones: (restaurantStore.deliveryZones ?? []).map((z) => ({
              id: z.id,
              cost: z.cost,
              pointsJson: JSON.stringify(z.points ?? []),
              enabled: z.enabled,
            })),
            strictOutsideZone: true,
          });
        } catch {
          return { key: 'posV2.errOutsideZone' };
        }
      }
    }

    return null;
  }

  function recomputeTotals(draftId: string): void {
    const draft = findDraft(draftId);
    if (!draft) return;
    draft.totals = draftTotals.computeDraftTotals(draft);
  }

  //#endregion

  //#region Persist to server (create / update)

  function buildOrderPayload(draft: IPosDraft, forUpdate: boolean) {
    const restaurant = restaurantStore.restaurant!;
    const branch = restaurantStore.selectedBranch!;
    const isSeat = draft.type === OrderType.Seat;

    return {
      restaurantId: restaurant.id,
      branchId: branch.id,
      orderType: draft.type,
      // Every cart item ALWAYS includes userComment (string | null, never
      // omitted — the backend DTO rejects an omitted key).
      cartItems: draft.cartLines.map((l) => ({
        itemId: l.itemId,
        quantity: l.quantity,
        userComment: l.userComment,
        unitPrice: l.unitPrice,
        totalPrice: l.totalPrice,
        index: l.index,
        optionValues: l.optionValues,
      })),
      userComment: draft.userComment,
      deliveryInfo:
        draft.type === OrderType.Delivery ? draft.deliveryInfo : null,
      paymentMethod: null,
      customerData: {
        name: draft.customerData.name,
        phone: draft.customerData.phone,
      },
      customFieldValues: draft.customFieldValues,
      customerId: draft.customerId ?? undefined,
      // Same create/update semantics as covers: absent on create when unset,
      // explicit null on update to clear the stored value server-side.
      clubMemberId: forUpdate
        ? draft.clubMemberId ?? null
        : draft.clubMemberId ?? undefined,
      // Create only: the server consumes the code for the new order ("one
      // code, one order"). Never sent on update (the update DTO has no field).
      clubCode: !forUpdate && draft.clubCode ? draft.clubCode : undefined,
      tableId: isSeat ? draft.tableId : undefined,
      // Create: only sent when set (absent = none). Update: always explicit —
      // a number updates, null clears the stored value server-side.
      covers: isSeat
        ? forUpdate
          ? draft.covers ?? null
          : draft.covers ?? undefined
        : undefined,
      manual: true,
      deviceId: restaurantStore.deviceId,
    };
  }

  function assertCanPersist(draft: IPosDraft): void {
    if (restaurantStore.selectedBranch?.ordersMode === OrderMode.WhatsApp) {
      throw new PosV2Error('posV2.errWhatsAppMode');
    }

    if (!socketStore.isConnected) {
      throw new PosV2Error('posV2.errNoConnection');
    }

    const validationError = getValidationError(draft);
    if (validationError) {
      throw new PosV2Error(validationError.key, validationError.params);
    }
  }

  function getOrderTitle(order: IOrder): string {
    const tableName =
      order.table?.name ??
      restaurantStore.tables?.find((t) => t.id === order.tableId)?.name ??
      null;
    return getOrderTitleForOrder(order, tableName);
  }

  /**
   * Makes sure a tab shows `order` without stealing focus: a background bind
   * must never unmount the workspace the user is typing in (the tab strip
   * reveals the new tab). Unlike `openOrderTab`, this is synchronous and
   * never reassigns `activeDraftId`.
   */
  function ensureViewTab(order: IOrder): void {
    if (drafts.value.some((d) => d.orderId === order.id)) return;
    const stub = createEmptyDraft(order.type, 'view');
    stub.orderId = order.id;
    drafts.value.push(stub);
  }

  /** Create drafts whose `persistCreate` is still running (they bind themselves). */
  const inFlightCreateDraftIds = new Set<string>();

  /**
   * Binds a create draft to the order persisted under its localId. Idempotent
   * and shared by the request path and the late-bind watcher.
   *
   * With `replayCandidate` false (plain happy path) the tab simply becomes the
   * order's view. Otherwise the draft may no longer describe the order (edited
   * after an unconfirmed attempt or during the request, restored twice), so
   * content parity decides:
   * - same content, or an emptied cart (nothing left to save) -> view;
   * - different, order editable and same type -> edit draft keeping the user's
   *   cart (the order's identity and server-owned fields are adopted);
   * - otherwise the cart cannot be applied to that order: the draft gets a new
   *   localId (it is a new order) and the landed order gets its own view tab.
   */
  function bindCreatedOrder(
    draftId: string,
    landed: IOrder,
    ctx: { replayCandidate: boolean; source: 'request' | 'late' }
  ): void {
    const draft = findDraft(draftId);
    if (!draft || draft.mode !== 'create' || draft.orderId) return;

    // The store copy is at least as fresh as an HTTP replay; a stale
    // baselineUpdatedAt would raise the edit-conflict banner.
    const order = findOrderByLocalId(draft.localId) ?? landed;
    const outstandingMs =
      draft.createSubmittedAt === null
        ? 0
        : Date.now() - draft.createSubmittedAt;
    draft.createSubmittedAt = null;

    const patch = orderToDraftPatch(order);
    const title = getOrderTitle(order);
    const comparison = ctx.replayCandidate
      ? compareOrderContent(draft, patch)
      : null;

    const logContext = {
      localId: draft.localId,
      orderId: order.id,
      source: ctx.source,
      outstandingMs,
    };

    // An emptied cart has nothing to save (empty edits fail validation), so
    // the order is shown as created and can be canceled from the view.
    const nothingToSave = draft.cartLines.length === 0;
    if (!comparison || comparison.same || nothingToSave) {
      if (comparison && !comparison.same) {
        console.warn('[PosCreate] diverged bind', {
          ...logContext,
          outcome: 'view',
        });
      }
      applyOrderPatch(draftId, patch);
      setMode(draftId, 'view');
      ui.showToast(
        AlertType.Success,
        String(i18n.t('posV2.createdToast', { title }))
      );
      return;
    }

    if (isOrderEditable(order) && order.type === draft.type) {
      // The edit-conflict banner reads the order from the orders store; seed
      // it only when the HTTP response beat the socket echo. Editable orders
      // always pass updateOrder's pending/new gate, addOrder(false) merely
      // skips its "not found" console.error.
      if (
        ordersStore.orders &&
        !ordersStore.orders.some((o) => o.id === order.id)
      ) {
        ordersStore.addOrder(order, false);
      }

      // Equal multiset: adopt the server's indexes so the update diff is
      // all-equal (no cancel permission, no reprint). Otherwise the user's
      // lines keep their indexes (echoed on create, so the diff keys on them)
      // unless one collides with a different persisted item.
      const lines: IRekeyedCartLines = comparison.linesSame
        ? { cartLines: patch.cartLines, nextIndex: patch.nextIndex }
        : rekeyCollidingCartLines(
            draft.cartLines,
            order.orderItems ?? [],
            Math.max(draft.nextIndex, patch.nextIndex)
          );

      Object.assign(draft, {
        orderId: order.id,
        // Covers are server-owned once persisted (the table session wins): a
        // draft-side null would clear them on save. The table stays the
        // user's; it is part of the content parity above.
        covers: patch.covers,
        baselineUpdatedAt: patch.baselineUpdatedAt,
        baselineDeliveryLat: patch.baselineDeliveryLat,
        baselineDeliveryLng: patch.baselineDeliveryLng,
        discounts: patch.discounts,
        cartLines: lines.cartLines,
        nextIndex: lines.nextIndex,
        // The create consumed the identification code: the edit draft keeps
        // the member only.
        clubCode: null,
        mode: 'edit',
        isDirty: true,
      } satisfies Partial<IPosDraft>);
      touchDraft(draft);
      recomputeTotals(draftId);

      console.warn('[PosCreate] diverged bind', {
        ...logContext,
        outcome: 'edit',
      });
      ui.showToast(
        AlertType.Info,
        String(i18n.t('posV2.createBoundEditToast', { title }))
      );
      return;
    }

    draft.localId = uuidv4();
    // The landed order consumed the identification code: the split draft is
    // a new create and must not re-send it.
    draft.clubCode = null;
    ensureViewTab(order);

    console.warn('[PosCreate] diverged bind', {
      ...logContext,
      outcome: 'split',
    });
    ui.showToast(
      AlertType.Info,
      String(i18n.t('posV2.createBoundSplitToast', { title }))
    );
  }

  async function persistCreate(draftId: string): Promise<IOrder> {
    const draft = findDraft(draftId);
    if (!draft) throw new Error('Draft not found');
    if (draft.mode !== 'create' || draft.orderId) {
      throw new Error('Draft is not a create draft');
    }

    assertCanPersist(draft);

    // Two tabs that restored the same persisted draft share the localId: when
    // the other tab already created the order, the request would only return
    // that same row, so reconcile by content right away.
    const knownBefore = findOrderByLocalId(draft.localId);
    if (knownBefore) {
      bindCreatedOrder(draftId, knownBefore, {
        replayCandidate: true,
        source: 'request',
      });
      return knownBefore;
    }

    const payload = {
      ...buildOrderPayload(draft, false),
      // Stable per-draft key: retries after a timed-out-but-persisted create
      // hit the backend idempotency and return the original order instead of
      // duplicating it.
      localId: draft.localId,
    };

    const attemptStartedAt = Date.now();
    const hadUnconfirmed = draft.createSubmittedAt !== null;
    // Direct assignment: arming is not user work (no touch).
    if (!hadUnconfirmed) draft.createSubmittedAt = attemptStartedAt;
    inFlightCreateDraftIds.add(draftId);

    try {
      // The server notifies the staff room right after commit, usually before
      // the HTTP response returns, so the socket-fed store is an equally valid
      // confirmation (same localId => same row, unique index). Whichever lands
      // first wins; a lost/timed-out response no longer fails a created order.
      const landed = waitForOrderByLocalId(draft.localId);
      let order: IOrder;
      try {
        order = await Promise.race([
          OrdersService.v2Create(payload),
          landed.promise,
        ]);
      } catch (error) {
        // The watcher flushes on a microtask; the order may have landed in the
        // same tick the request failed.
        const committed = findOrderByLocalId(draft.localId);
        if (committed) {
          order = committed;
        } else {
          const axiosError =
            error != null && axios.isAxiosError(error) ? error : null;
          const status = axiosError?.response?.status ?? null;
          console.warn('[PosCreate] create request failed', {
            restaurantId: restaurantStore.restaurant?.id ?? null,
            branchId: restaurantStore.selectedBranch?.id ?? null,
            localId: draft.localId,
            socketConnected: socketStore.isConnected,
            status,
            code: axiosError?.code ?? null,
            message: error instanceof Error ? error.message : String(error),
          });
          // Transport failure or any server-side failure (5xx): unknown whether
          // the row exists, and a same-tab retry is idempotent. Only 4xx is a
          // definitive "not created" and keeps its server message.
          const unconfirmed =
            isRetryableRequestError(error) ||
            (status !== null && status >= 500);
          // A definitive failure of the only outstanding attempt disarms the
          // late-bind watcher; an earlier unconfirmed attempt stays armed.
          if (!unconfirmed && !hadUnconfirmed) draft.createSubmittedAt = null;
          throw unconfirmed
            ? new PosV2Error('posV2.errCreateUnconfirmed')
            : error;
        }
      } finally {
        landed.stop();
      }

      // Fallback: if the socket event hasn't landed the order in 1s, fetch it
      // (restaurantId is required — see refreshOrder's contract).
      const restaurantId = restaurantStore.restaurant?.id;
      if (restaurantId) {
        setTimeout(() => {
          if (ordersStore.orders?.find((o) => o.id === order.id)) return;
          OrdersService.v2GetOrderById(restaurantId, order.id)
            .then((fetched) => {
              ordersStore.updateOrder(order.id, fetched);
            })
            .catch((err) => {
              console.error('Error refreshing order after create', err);
            });
        }, 1000);
      }

      // The tab may have been closed mid-request.
      const live = findDraft(draftId);
      if (live) {
        bindCreatedOrder(draftId, order, {
          replayCandidate:
            hadUnconfirmed || live.lastTouchedAt > attemptStartedAt,
          source: 'request',
        });
      }

      return order;
    } finally {
      inFlightCreateDraftIds.delete(draftId);
    }
  }

  /**
   * True when saving this edit would need `PermissionType.OrdersCancel`
   * (mirrors the backend `didRemoveItems` escalation). The UI checks this to
   * decide whether to collect PIN auth before calling `persistUpdate`.
   */
  async function updateNeedsCancelPermission(
    draftId: string
  ): Promise<boolean> {
    const draft = findDraft(draftId);
    if (!draft?.orderId) return false;

    const order = await resolveOrderById(draft.orderId);
    return getOrderEditRequiresCancel(order.orderItems ?? [], draft.cartLines);
  }

  async function persistUpdate(
    draftId: string,
    authData?: IAuthData
  ): Promise<IOrder> {
    const draft = findDraft(draftId);
    if (!draft) throw new Error('Draft not found');
    if (!draft.orderId) throw new Error('Draft is not persisted');

    assertCanPersist(draft);

    const payload = {
      ...buildOrderPayload(draft, true),
      // Optimistic-concurrency baseline: the backend rejects the update with
      // a 400 when the order changed since this draft was hydrated.
      baselineUpdatedAt: draft.baselineUpdatedAt ?? undefined,
      ...(authData ? { authData } : {}),
    };

    let order = await OrdersService.v2Update(draft.orderId, payload);

    // Defensive: older API builds respond to the update with an empty body —
    // fetch the updated order so callers always get a real IOrder.
    if (!order?.id) {
      const restaurantId = restaurantStore.restaurant?.id;
      if (!restaurantId) throw new PosV2Error('posV2.errOrderNotFound');
      order = await OrdersService.v2GetOrderById(restaurantId, draft.orderId);
    }

    ordersStore.updateOrder(order.id, order);
    updateDraft(draftId, { isDirty: false });

    return order;
  }

  //#endregion

  //#region Persistence (localStorage) + branch lifecycle

  function storageKeyFor(restaurantId: number, branchId: number): string {
    return `${PERSISTENCE_KEY}:${restaurantId}:${branchId}`;
  }

  function serializeState(
    restaurantId: number,
    branchId: number
  ): IPersistedState {
    return {
      version: PERSISTENCE_VERSION,
      restaurantId,
      branchId,
      activeDraftId: activeDraftId.value,
      drafts: drafts.value.map((d) => {
        if (d.mode === 'view' && d.orderId) {
          return { id: d.id, mode: 'view', orderId: d.orderId, type: d.type };
        }
        // The identification code never reaches storage: it is dead after
        // 5 minutes and a restored draft must ask the member for a fresh one.
        return { ...d, clubCode: null };
      }),
    };
  }

  /**
   * Writes the current drafts under the given scope's key. Storage is keyed
   * per restaurant+branch so switching branches never overwrites another
   * branch's saved drafts. Returns whether the write actually landed.
   */
  function persistScopedState(restaurantId: number, branchId: number): boolean {
    try {
      localStorage.setItem(
        storageKeyFor(restaurantId, branchId),
        JSON.stringify(serializeState(restaurantId, branchId))
      );
      return true;
    } catch (e) {
      console.error('Error persisting POS drafts', e);
      return false;
    }
  }

  function persistToStorage(): void {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;
    // Each successful flush consumes the dirty flag: without this, a tab that
    // mutated once would keep flushing its (increasingly stale) in-memory
    // snapshot on every unload, clobbering newer writes from other tabs. A
    // failed write (quota/blocked storage) keeps the flag so the unload flush
    // still gets a chance to save the drafts.
    if (persistScopedState(restaurantId, branchId)) {
      hasLocalMutations = false;
    }
  }

  /**
   * Guarded flush: only tabs with unflushed local mutations may write — an
   * idle admin tab unloading with a stale in-memory snapshot used to resurrect
   * closed drafts and drop other tabs' edits (F5). The guard also disarms a
   * debounced persist left over from before a branch switch (the switch
   * resets the flag), which would otherwise rewrite the new scope's key with
   * its just-restored snapshot and could clobber another tab's save.
   */
  function flushIfDirty(): void {
    if (!hasLocalMutations) return;
    persistToStorage();
  }

  // F5 flags (see flushIfDirty). suppressPersistence is a counter, not a
  // boolean: restore failures fall back to clearAllDrafts(), nesting two
  // suppress windows — a boolean would un-suppress while the outer window is
  // still open. The persistence watcher flushes synchronously, so the
  // increment/decrement pairs delimit the exact mutations they wrap.
  let hasLocalMutations = false;
  let suppressPersistence = 0;

  const VALID_MODES: TPosDraftMode[] = ['create', 'edit', 'view'];
  const VALID_ORDER_TYPES = Object.values(OrderType);

  /**
   * Shape validation for persisted entries so corrupted/hand-edited storage
   * can never brick the POS (a draft with e.g. `cartLines: null` used to
   * throw mid-restore and get re-persisted, failing every subsequent load).
   */
  function isValidPersistedDraft(
    entry: IPosDraft | IPersistedViewStub | null | undefined
  ): boolean {
    if (!entry || typeof entry.id !== 'string') return false;
    if (!VALID_MODES.includes(entry.mode)) return false;

    if (entry.mode === 'view') {
      return Number.isFinite((entry as IPersistedViewStub).orderId);
    }

    const full = entry as IPosDraft;
    if (!VALID_ORDER_TYPES.includes(full.type)) return false;

    // An edit draft without its order id would render a Create button and
    // duplicate the order it was editing.
    if (full.mode === 'edit' && !Number.isFinite(full.orderId)) return false;

    // A corrupted nextIndex would mint colliding line indexes on the next
    // add, which the backend diff rejects.
    if (!Number.isFinite(full.nextIndex)) return false;

    if (!Array.isArray(full.cartLines)) return false;
    const linesValid = full.cartLines.every(
      (line) =>
        !!line &&
        Number.isFinite(line.index) &&
        Number.isFinite(line.itemId) &&
        Number.isFinite(line.quantity)
    );
    if (!linesValid) return false;

    if (
      !full.customerData ||
      typeof full.customerData !== 'object' ||
      typeof full.customerData.name !== 'string' ||
      typeof full.customerData.phone !== 'string'
    ) {
      return false;
    }

    return (
      !!full.customFieldValues &&
      typeof full.customFieldValues === 'object' &&
      !Array.isArray(full.customFieldValues)
    );
  }

  function restoreFromStorage(): boolean {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return false;

    // Suppress the (sync-flush) persistence watcher for the whole restore:
    // without this, every tab rewrites the storage key ~500ms after restoring
    // and can clobber another tab's save made inside that window.
    suppressPersistence++;
    // Outside try so the catch can drop a corrupt payload from the right key.
    let usedLegacyKey = false;
    try {
      let parsed: IPersistedState | null = null;
      try {
        // Legacy fallback: drafts saved before storage was branch-scoped lived
        // under the bare key. Read it once and migrate after a successful restore.
        let raw = localStorage.getItem(storageKeyFor(restaurantId, branchId));
        if (!raw) {
          raw = localStorage.getItem(PERSISTENCE_KEY);
          usedLegacyKey = raw !== null;
        }
        if (!raw) return false;
        parsed = JSON.parse(raw) as IPersistedState;
      } catch {
        return false;
      }

      if (
        !parsed ||
        parsed.version !== PERSISTENCE_VERSION ||
        parsed.restaurantId !== restaurantId ||
        parsed.branchId !== branchId ||
        !Array.isArray(parsed.drafts)
      ) {
        return false;
      }

      const restored: IPosDraft[] = [];
      for (const entry of parsed.drafts) {
        if (!isValidPersistedDraft(entry)) {
          console.warn('Skipping invalid persisted POS draft', entry);
          continue;
        }

        if (entry.mode === 'view') {
          // View stubs rehydrate lazily in the workspace. Restore the stored
          // type so the tab label doesn't fall back to "Mesa" for Away/Delivery.
          const stubEntry = entry as IPersistedViewStub;
          const stubType =
            stubEntry.type && VALID_ORDER_TYPES.includes(stubEntry.type)
              ? stubEntry.type
              : OrderType.Seat;
          const stub = createEmptyDraft(stubType, 'view');
          stub.id = stubEntry.id;
          stub.orderId = stubEntry.orderId;
          restored.push(stub);
          continue;
        }

        const full = entry as IPosDraft;
        // Spread over an empty draft so every key exists (reactive +
        // future-proof against shape additions). Entries persisted before
        // `localId` existed inherit the base's fresh one.
        const base = createEmptyDraft(full.type ?? OrderType.Seat, full.mode);
        const merged = {
          ...base,
          ...full,
          localId: full.localId || base.localId,
          // Same normalization pattern as localId: entries persisted before
          // the field existed (or corrupted values) inherit base's fresh
          // Date.now().
          lastTouchedAt: Number.isFinite(full.lastTouchedAt)
            ? full.lastTouchedAt
            : base.lastTouchedAt,
          createSubmittedAt: Number.isFinite(full.createSubmittedAt)
            ? full.createSubmittedAt
            : null,
          // Storage never holds a code; a hand-edited payload must not either.
          clubCode: null,
        };
        // Pristine create drafts older than the TTL are dead weight from past
        // sessions. Reuses the store's canonical pristine predicate so any
        // user data — cart lines, table prefill, customer data, comments… —
        // always keeps the draft: never silently delete work; the age chip in
        // the tab strip flags it instead. Entries persisted before
        // lastTouchedAt existed inherit base's fresh Date.now() and age from
        // now on.
        if (
          isEmptyUneditedDraft(merged) &&
          Date.now() - merged.lastTouchedAt > EMPTY_CREATE_DRAFT_TTL_MS
        ) {
          continue;
        }
        restored.push(merged);
      }

      if (!restored.length) {
        // Everything in the payload was invalid or TTL-dead for this scope, so
        // the legacy copy is worthless too — drop it or it gets re-parsed (and
        // re-dropped) on every boot.
        if (usedLegacyKey) {
          try {
            localStorage.removeItem(PERSISTENCE_KEY);
          } catch {
            // Ignore: nothing restorable was lost.
          }
        }
        return false;
      }

      drafts.value = restored;
      activeDraftId.value =
        parsed.activeDraftId &&
        restored.some((d) => d.id === parsed.activeDraftId)
          ? parsed.activeDraftId
          : restored[0].id;

      // Settings/prices may have changed since serialization.
      for (const draft of restored) {
        if (draft.mode !== 'view') recomputeTotals(draft.id);
      }

      if (usedLegacyKey) {
        // Only drop the legacy key once the scoped copy actually landed:
        // removing it after a failed (quota) write would delete the only
        // durable copy of the drafts.
        if (persistScopedState(restaurantId, branchId)) {
          try {
            localStorage.removeItem(PERSISTENCE_KEY);
          } catch {
            // Ignore: the scoped copy is already written.
          }
        }
      }

      return true;
    } catch (e) {
      // Defense in depth: any unforeseen throw mid-restore must not brick the
      // POS. Reset to a clean state and drop the corrupt payload so it cannot
      // re-brick (or re-reset) the next load — from whichever key fed it.
      console.error('Error restoring POS drafts, resetting', e);
      clearAllDrafts();
      try {
        localStorage.removeItem(storageKeyFor(restaurantId, branchId));
        if (usedLegacyKey) localStorage.removeItem(PERSISTENCE_KEY);
      } catch {
        // Ignore: state is already clean in memory.
      }
      return false;
    } finally {
      suppressPersistence--;
    }
  }

  function clearAllDrafts(): void {
    suppressPersistence++;
    try {
      drafts.value = [];
      activeDraftId.value = null;
      createDraft();
    } finally {
      suppressPersistence--;
    }
  }

  // Debounces the guarded flush (not the raw write): a leftover timer from
  // before a branch switch finds the flag reset and no-ops.
  const persistDebounced = useDebounceFn(flushIfDirty, 500);

  // flush: 'sync' is load-bearing: the default (batched) flush fires once per
  // tick, so a user mutation sharing a tick with suppressed restore/clear
  // mutations would be swallowed by the suppressed callback. Synchronous
  // flushing evaluates the suppress counter at mutation time, making the
  // window exact. The callback is trivial (flag + debounce reset), so firing
  // per-mutation is cheap.
  watch(
    [drafts, activeDraftId],
    () => {
      if (suppressPersistence > 0) return;
      hasLocalMutations = true;
      persistDebounced();
    },
    { deep: true, flush: 'sync' }
  );

  // The debounce resets on every change, so a reload/close right after an
  // edit would lose it. Flush synchronously when the page is about to go away
  // ('pagehide' also covers mobile browsers where 'beforeunload' is skipped).
  // No explicit window target: VueUse falls back to its SSR-safe default,
  // which is a no-op in the Node test environment.
  useEventListener('beforeunload', flushIfDirty);
  useEventListener('pagehide', flushIfDirty);

  let initializedForKey: string | null = null;

  watch(
    () =>
      restaurantStore.restaurant?.id && restaurantStore.selectedBranch?.id
        ? `${restaurantStore.restaurant.id}|${restaurantStore.selectedBranch.id}`
        : null,
    (key, oldKey) => {
      if (!key || key === initializedForKey) return;

      const previousKey = initializedForKey ?? oldKey;
      initializedForKey = key;

      // Flush the outgoing branch's drafts under its own key before replacing
      // them, so switching back (or reloading later) is lossless. Gated on
      // hasLocalMutations for the same reason as the unload flush.
      if (previousKey && hasLocalMutations) {
        const [prevRestaurantId, prevBranchId] = previousKey
          .split('|')
          .map(Number);
        if (prevRestaurantId && prevBranchId) {
          persistScopedState(prevRestaurantId, prevBranchId);
        }
      }
      hasLocalMutations = false;

      try {
        if (restoreFromStorage()) return;
      } catch (e) {
        // restoreFromStorage handles its own failures; this is a last resort.
        console.error('Error restoring POS drafts on scope switch', e);
      }

      // Nothing restorable for this branch: start fresh.
      clearAllDrafts();
    },
    { immediate: true }
  );

  //#endregion

  //#region Auto-prune of order-bound tabs
  // Placement is load-bearing: this watcher is `immediate`, and immediate
  // watchers fire in declaration order during store setup — the branch-scope
  // watcher above (also `immediate`) must restore the persisted drafts FIRST
  // so the boot pass sees them. `immediate: true` itself is required because
  // the store can be instantiated when ordersStore.orders is already non-null
  // (lazy store init, HMR): without it, knownOrderIds would stay null until
  // the next membership change and the first change would then run with boot
  // semantics at a random later time, while stale restored tabs sit
  // untouched. An "order-bound" tab is any draft with `orderId` set (view
  // stubs, view tabs, edit tabs).

  // Last-seen live order-id set. null = no full snapshot yet for the current
  // branch scope (ordersStore.clear() resets both orders and snapshotLoadedAt,
  // so boot semantics re-arm automatically per branch).
  let knownOrderIds: Set<number> | null = null;

  // Key string so the watcher only fires when membership actually changes
  // (state.orders mutates on every WS payload; a Set computed would retrigger
  // on every recompute). null until a FULL snapshot has been applied: a WS
  // event arriving before the first snapshot initializes state.orders as a
  // partial one-order array (orders.store addOrder), and a boot prune against
  // that partial set would wrongly close valid tabs. Gating on
  // snapshotLoadedAt also guarantees a fire when the snapshot lands even if
  // it doesn't change membership.
  const liveOrderIdsKey = computed<string | null>(() =>
    ordersStore.orders === null || ordersStore.snapshotLoadedAt === null
      ? null
      : ordersStore.orders
          .map((o) => o.id)
          .sort((a, b) => a - b)
          .join(',')
  );

  watch(
    liveOrderIdsKey,
    (key) => {
      if (key === null) {
        knownOrderIds = null; // branch switch / pre-snapshot: re-arm boot semantics
        return;
      }
      const current = new Set((ordersStore.orders ?? []).map((o) => o.id));
      const previous = knownOrderIds;
      knownOrderIds = current;
      pruneOrderTabs(current, previous);
    },
    // Load-bearing (see region intro): a store instantiated after orders
    // already loaded must still run its boot pass.
    { immediate: true }
  );

  /**
   * Closes tabs whose persisted order is no longer live.
   * Boot pass (previous === null, first snapshot after restore/branch switch):
   * absence-based and includes dirty edit tabs — their edits are unsavable
   * anyway (stale baseline + backend rejects edits to archived orders).
   * Live pass: removal-delta only (an order that LEFT the set). Never
   * absence-based: after persistCreate the tab holds an orderId before the
   * order reaches the store (WS lag), and must not be closed. Dirty edit tabs
   * are spared live — activeDraftEditConflict already banners them.
   */
  function pruneOrderTabs(
    current: Set<number>,
    previous: Set<number> | null
  ): void {
    const isBoot = previous === null;
    const toClose = drafts.value.filter((d) => {
      if (!d.orderId || current.has(d.orderId)) return false;
      if (isBoot) return true;
      if (!previous.has(d.orderId)) return false;
      return !(d.mode === 'edit' && d.isDirty);
    });
    if (!toClose.length) return;

    const closesActive = toClose.some((d) => d.id === activeDraftId.value);
    for (const d of toClose) closeDraft(d.id, true);

    // Boot cleanup is silent; live closes of the focused tab get one toast so
    // the workspace switching under the user isn't confusing.
    if (!isBoot && closesActive) {
      ui.showToast(AlertType.Info, String(i18n.t('posV2.tabAutoClosed')));
    }
  }

  //#endregion

  //#region Late binding of create drafts
  // Declared after the prune region on purpose: immediate watchers fire in
  // declaration order, so the persisted drafts are restored and the boot prune
  // has run before this pass. No snapshot gate is needed here: a positive
  // localId match is proof the order exists (absence, which prune acts on, is
  // not). In-flight drafts are skipped because `persistCreate` binds them
  // itself, which keeps the plain happy path free of content comparison.

  function isArmedCreateDraft(draft: IPosDraft): boolean {
    return (
      draft.mode === 'create' &&
      !draft.orderId &&
      draft.createSubmittedAt !== null
    );
  }

  // Key string so the watcher only fires when the bindable set changes. Drafts
  // are read first: while nothing is armed the computed has no dependency on
  // ordersStore.orders (which mutates on every WS payload).
  const lateCreateBindKey = computed<string>(() => {
    const armed = drafts.value.filter(isArmedCreateDraft);
    if (!armed.length) return '';
    const orders = ordersStore.orders;
    if (!orders) return '';
    const pairs: string[] = [];
    for (const draft of armed) {
      const order = orders.find((o) => o.localId === draft.localId);
      if (order) pairs.push(`${draft.id}:${order.id}`);
    }
    return pairs.sort().join(',');
  });

  watch(
    lateCreateBindKey,
    (key) => {
      if (!key) return;
      for (const draft of [...drafts.value]) {
        if (!isArmedCreateDraft(draft) || inFlightCreateDraftIds.has(draft.id)) {
          continue;
        }
        const order = findOrderByLocalId(draft.localId);
        if (order) {
          bindCreatedOrder(draft.id, order, {
            replayCandidate: true,
            source: 'late',
          });
        }
      }
    },
    { immediate: true }
  );

  //#endregion

  const actions = {
    createDraft,
    closeDraft,
    setActiveDraft,
    setMode,
    updateDraft,
    addCartLine,
    editCartLine,
    removeCartLine,
    setQuantity,
    clearCart,
    applyCustomer,
    applyClubMember,
    loadFromOrder,
    refreshEditBaseline,
    openOrderTab,
    syncViewDraftType,
    getValidationError,
    getActiveCustomFields,
    recomputeTotals,
    persistCreate,
    persistUpdate,
    updateNeedsCancelPermission,
    isEmptyUneditedDraft,
    flushIfDirty,
  };

  return {
    drafts,
    activeDraftId,
    activeDraft,
    activeDraftEditConflict,
    ...actions,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePosDraftsStore, import.meta.hot));
}
